// Kanban — durable nudge delivery (ENG-6785).
//
// enqueueKanbanNotice wraps POST /host/kanban/notify, which enqueues the kanban
// "check your board" nudge as a direct_chat_messages notice (ENG-6381 kind) on
// the verified direct-chat rail instead of firing it over unverified tmux
// send-keys (which always returned delivered=false post-ENG-6345, so every
// kanban inject logged "failed" and the board never progressed). Best-effort —
// never throws to the caller; a fault logs and reports not-ok so the manager can
// fall back to the legacy send-keys path.

import { createHash } from 'node:crypto';
import { api } from '../../api-client.js';
import { log } from '../runtime.js';

export interface EnqueueKanbanNoticeOptions {
  agentId: string;
  /** The full nudge text (KANBAN_CHECK_COMMAND, optionally + run marker). */
  content: string;
  /** Attribution run id, carried in the notice payload (best-effort). */
  runId?: string | null;
  actionableCount?: number;
}

/**
 * Enqueue the kanban-check nudge onto the direct-chat durable rail.
 *
 * Returns `true` when the rail accepted the nudge — both a fresh enqueue and an
 * `already_pending` skip count as success (the durable queue still holds an
 * outstanding notice, so there is nothing to fall back for). Returns `false`
 * only on a transport/route error, signalling the caller to use the legacy
 * send-keys path.
 */
export async function enqueueKanbanNotice(opts: EnqueueKanbanNoticeOptions): Promise<boolean> {
  try {
    const res = await api.post<{ ok?: boolean; enqueued?: boolean; reason?: string }>(
      '/host/kanban/notify',
      {
        agent_id: opts.agentId,
        content: opts.content,
        run_id: opts.runId ?? undefined,
        actionable_count: opts.actionableCount,
      },
    );
    return res.ok === true;
  } catch (err) {
    // Hash the raw error text so durable manager logs don't carry potentially
    // sensitive payloads (per CLAUDE.md log policy).
    const errText = err instanceof Error ? err.message : String(err);
    const errId = createHash('sha256').update(errText).digest('hex').slice(0, 12);
    log(`[kanban] notice enqueue failed for agent_id=${opts.agentId} error_id=${errId}`);
    return false;
  }
}
